import { Flex } from '../../../../../styled-components';
import { useTranslation } from 'react-i18next';
import { PriceInformationFormValues } from '../PriceInformationStep';
import styled from 'styled-components';
import { Interval } from '../../../../../contexts';

/**
 * The type for the "values" Component prop.
 * This higher order type is made in order not to tie the Component down to the
 * Formik implementation of the "values" object.
 */
type PriceAlarmFormValues = {
  [key in keyof Pick<PriceInformationFormValues, 'priceAlarm' | 'priceAlarmExpiration'>]: number | string | undefined;
};

/**
 * The type for the "errors" Component prop.
 * This higher order type is made in order not to tie the Component down to the
 * Formik implementation of the "errors" object.
 */
type PriceAlarmFormErrors = {
  [key in keyof Pick<PriceInformationFormValues, 'priceAlarm' | 'priceAlarmExpiration'>]: string | undefined;
};

/**
 * Container for the fields.
 * Useful for the responsiveness of the Component.
 */
const Container = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;

  @media (min-width: 576px) {
    flex-direction: row;
  }
`;

/**
 * The label + input + tag.
 */
const Field = styled(Flex)`
  margin-right: 0;

  @media (min-width: 576px) {
    margin-right: 1.5rem;
  }
`;

/**
 * The PriceAlarm props.
 */
interface PriceAlarmProps {
  /**
   * The callback to pass to the inputs as provided by Formik.
   */
  handleChange: {
    /** Classic React change handler, keyed by input name */
    // eslint-disable-next-line
    (e: React.ChangeEvent<any>): void;
    /** Preact-like linkState. Will return a handleChange function.  */
    // eslint-disable-next-line
    <T = string | React.ChangeEvent<any>>(field: T): T extends React.ChangeEvent<any>
      ? void // eslint-disable-next-line
      : (e: string | React.ChangeEvent<any>) => void;
  };

  /**
   * The current price of the product.
   */
  actualPrice: number;

  /**
   * The values for the inputs.
   */
  values: PriceAlarmFormValues;

  /**
   * The possible errors for the form values.
   */
  errors: PriceAlarmFormErrors;
}

export default function PriceAlarm({ actualPrice, values, errors, handleChange }: PriceAlarmProps) {
  const { t } = useTranslation();

  return (
    <Container>
      <Field direction="column">
        <label htmlFor="actualPriceId" className="form-label">
          <span>{t('PriceInformation.Configurators.PriceAlarm.CurrentPrice')}</span>
        </label>
        <input id="actualPriceId" className="form-control" value={actualPrice} disabled />
        <p>
          <span className="d-block">{t('PriceInformation.Configurators.FuelUnitCode')}</span>
          <span className="d-block">{t('PriceInformation.Configurators.TaxInfo')}</span>
        </p>
      </Field>

      <Field direction="column">
        <label htmlFor="priceAlarmId" className="form-label">
          <span>{t('PriceInformation.Configurators.PriceAlarm.PriceAlarm')}</span>
        </label>
        <Flex direction="column" className="position-relative">
          <input
            id="priceAlarmId"
            className={`form-control ${!!errors.priceAlarm && 'is-invalid'}`}
            name="priceAlarm"
            value={values.priceAlarm}
            onChange={handleChange}
          />

          <div className="invalid-tooltip">{errors.priceAlarm}</div>
        </Flex>

        <p>
          <span className="d-block">{t('PriceInformation.Configurators.FuelUnitCode')}</span>
          <span className="d-block">{t('PriceInformation.Configurators.TaxInfo')}</span>
        </p>
      </Field>

      <Field direction="column">
        <label htmlFor="priceAlarmExpirationId" className="form-label">
          <span>{t('PriceInformation.Configurators.PriceAlarm.Expiration')}</span>
        </label>
        <Flex direction="column" className="position-relative">
          <select
            id="priceAlarmExpirationId"
            className={`form-select ${!!errors.priceAlarmExpiration && 'is-invalid'}`}
            name="priceAlarmExpiration"
            value={values.priceAlarmExpiration}
            onChange={handleChange}
          >
            <option value={Interval.ThreeDays}>{t('PriceInformation.Configurators.ThreeDays')}</option>
            <option value={Interval.SevenDays}>{t('PriceInformation.Configurators.SevenDays')}</option>
            <option value={Interval.TwoWeeks}>{t('PriceInformation.Configurators.TwoWeeks')}</option>
            <option value={Interval.FourWeeks}>{t('PriceInformation.Configurators.FourWeeks')}</option>
            <option value={Interval.EightWeeks}>{t('PriceInformation.Configurators.EightWeeks')}</option>
          </select>

          <div className="invalid-tooltip">{errors.priceAlarmExpiration}</div>
        </Flex>
      </Field>
    </Container>
  );
}
